import { Divider, Paragraph, Text, Title } from "~/components/ui";
import { type FunctionComponent } from "react";

/**
 * Public privacy policy — the URL declared in Google Play Console → App content and in App Store
 * Connect. It is matched **before** the Clerk gate in `app.tsx`: a store reviewer opens it with no
 * account, so gating it would fail review.
 *
 * Deliberately not translated through `@noa/i18n`: both languages render on the same page so the
 * document a reviewer (or a tester) lands on is complete regardless of browser locale, and the legal
 * text stays in one reviewable file instead of split across two locale JSONs.
 */
export const PRIVACY_POLICY_ROUTE = "/privacy";

// Replace with a shared, monitored mailbox if one is created — Play requires a contact that answers.
const CONTACT_EMAIL = "daigles@lichens.ai";

const LAST_UPDATED_FR = "4 août 2026";
const LAST_UPDATED_EN = "August 4, 2026";

interface PolicySection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  /** Rendered after the bullet list, for a closing remark the list must not interrupt. */
  notes?: string[];
}

interface PolicyDocument {
  lang: string;
  title: string;
  lastUpdatedLabel: string;
  intro: string;
  sections: PolicySection[];
}

const FRENCH: PolicyDocument = {
  lang: "fr",
  title: "Politique de confidentialité",
  lastUpdatedLabel: `Dernière mise à jour : ${LAST_UPDATED_FR}`,
  intro:
    "Noa est un assistant conversationnel d'aide à la maintenance, accessible par une application web et une application mobile (iOS et Android). Cette politique décrit les données que l'application traite, les sous-traitants qui y ont accès, et la manière d'exercer vos droits. Elle s'applique aux deux applications, qui partagent le même service dorsal.",
  sections: [
    {
      title: "1. Accès réservé",
      paragraphs: [
        "Noa n'est pas ouvert au public. L'inscription libre est désactivée : un compte est créé uniquement sur invitation de l'organisation exploitante. Il n'existe aucun profil accessible sans authentification.",
      ],
    },
    {
      title: "2. Données traitées",
      bullets: [
        "Données de compte : adresse courriel, nom affiché et identifiant du fournisseur d'identité, transmis par notre prestataire d'authentification.",
        "Contenu des conversations : les questions que vous rédigez, les réponses générées, les documents et images cités en référence, ainsi que l'horodatage des échanges.",
        "Rétroaction : le pouce vers le haut ou vers le bas que vous attribuez à une réponse.",
        "Préférences : langue et thème d'affichage, conservés localement sur votre appareil.",
        "Journaux techniques : erreurs applicatives et données de diagnostic du service dorsal, nécessaires à l'exploitation.",
      ],
      paragraphs: [
        "L'application ne collecte pas votre position, votre carnet d'adresses, vos photos ni vos contacts, et n'utilise aucun traceur publicitaire.",
      ],
    },
    {
      title: "3. Fournisseurs de modèles de langage",
      paragraphs: [
        "Pour produire une réponse, le contenu de votre question et les extraits de documents pertinents sont transmis à des fournisseurs de modèles de langage tiers :",
      ],
      bullets: [
        "Anthropic (modèles Claude) — génération des réponses de conversation et extraction de contenu d'images lors de l'indexation des documents.",
        "OpenAI — calcul des représentations vectorielles (« embeddings ») utilisées pour retrouver les passages de documents pertinents.",
      ],
      notes: [
        "Ces fournisseurs traitent ces données pour le seul compte de l'organisation exploitante, dans le cadre de leurs conditions d'utilisation d'interface de programmation (API). N'y saisissez pas de renseignements personnels sensibles dont le traitement par un tiers ne serait pas souhaité.",
      ],
    },
    {
      title: "4. Autres sous-traitants",
      bullets: [
        "Clerk — authentification, gestion des sessions et de l'authentification à deux facteurs.",
        "Microsoft — connexion par compte professionnel (OAuth), lorsque vous choisissez cette méthode.",
        "Microsoft Azure — hébergement du service dorsal, de la base de données documentaire et de la base vectorielle.",
      ],
      paragraphs: [
        "Ces prestataires agissent pour notre compte et n'ont accès qu'aux données nécessaires à leur fonction. Aucune donnée n'est vendue, louée ni transmise à des courtiers de données ou à des annonceurs.",
      ],
    },
    {
      title: "5. Finalités",
      paragraphs: [
        "Les données ci-dessus servent exclusivement à : authentifier votre accès, répondre à vos questions, conserver l'historique de vos conversations pour que vous puissiez y revenir, améliorer la pertinence des réponses à partir de votre rétroaction, et assurer la sécurité et le bon fonctionnement du service.",
      ],
    },
    {
      title: "6. Transferts hors du pays",
      paragraphs: [
        "L'hébergement principal est situé au Canada. Les fournisseurs de modèles de langage et le prestataire d'authentification traitent les données sur des infrastructures pouvant se trouver aux États-Unis. L'utilisation de l'application implique ce transfert.",
      ],
    },
    {
      title: "7. Conservation",
      paragraphs: [
        "Les conversations sont conservées jusqu'à leur suppression par vous ou par l'organisation exploitante. La suppression d'une conversation depuis l'application la retire de la base de données. La suppression d'un compte entraîne celle des conversations associées, sur demande adressée au contact ci-dessous.",
      ],
    },
    {
      title: "8. Sécurité",
      paragraphs: [
        "Les échanges avec le service dorsal se font exclusivement en HTTPS. L'accès est protégé par un jeton d'authentification à durée de vie courte, conservé sur l'appareil dans le trousseau du système d'exploitation (Keychain sur iOS, Keystore sur Android). L'authentification à deux facteurs est activée sur le service d'authentification.",
      ],
    },
    {
      title: "9. Vos droits",
      paragraphs: [
        `Vous pouvez demander l'accès à vos données, leur rectification, leur suppression, ou une copie de vos conversations, en écrivant à ${CONTACT_EMAIL}. Nous répondons dans un délai raisonnable.`,
      ],
    },
    {
      title: "10. Enfants",
      paragraphs: [
        "L'application est un outil professionnel et n'est pas destinée aux personnes de moins de 16 ans. Aucun compte n'est créé pour un mineur.",
      ],
    },
    {
      title: "11. Modifications",
      paragraphs: [
        "Cette politique peut être mise à jour. La date en tête de page indique la dernière révision ; une modification significative sera communiquée aux utilisateurs invités.",
      ],
    },
    {
      title: "12. Contact",
      paragraphs: [`Pour toute question relative à cette politique : ${CONTACT_EMAIL}.`],
    },
  ],
};

const ENGLISH: PolicyDocument = {
  lang: "en",
  title: "Privacy Policy",
  lastUpdatedLabel: `Last updated: ${LAST_UPDATED_EN}`,
  intro:
    "Noa is a conversational maintenance-assistance assistant, available as a web application and as a mobile application (iOS and Android). This policy describes the data the application processes, the service providers that can access it, and how to exercise your rights. It covers both applications, which share the same backend.",
  sections: [
    {
      title: "1. Invitation-only access",
      paragraphs: [
        "Noa is not open to the public. Self-service sign-up is disabled: accounts are created only by invitation from the operating organization. There is no profile reachable without authentication.",
      ],
    },
    {
      title: "2. Data we process",
      bullets: [
        "Account data: email address, display name and identity-provider identifier, supplied by our authentication provider.",
        "Conversation content: the questions you write, the generated answers, the documents and images cited as sources, and message timestamps.",
        "Feedback: the thumbs-up or thumbs-down you attach to an answer.",
        "Preferences: language and appearance, stored locally on your device.",
        "Technical logs: application errors and backend diagnostics required to operate the service.",
      ],
      paragraphs: [
        "The application does not collect your location, photos, address book or contacts, and contains no advertising trackers.",
      ],
    },
    {
      title: "3. Language-model providers",
      paragraphs: [
        "To produce an answer, the content of your question and the relevant document excerpts are sent to third-party language-model providers:",
      ],
      bullets: [
        "Anthropic (Claude models) — generating chat answers, and extracting content from images during document ingestion.",
        "OpenAI — computing the vector embeddings used to retrieve relevant document passages.",
      ],
      notes: [
        "These providers process that data solely on behalf of the operating organization, under their API terms of service. Do not enter sensitive personal information you would not want processed by a third party.",
      ],
    },
    {
      title: "4. Other service providers",
      bullets: [
        "Clerk — authentication, session management and two-factor authentication.",
        "Microsoft — work-account sign-in (OAuth), when you choose that method.",
        "Microsoft Azure — hosting for the backend, the document database and the vector database.",
      ],
      paragraphs: [
        "These providers act on our behalf and receive only the data their function requires. No data is sold, rented, or shared with data brokers or advertisers.",
      ],
    },
    {
      title: "5. Purposes",
      paragraphs: [
        "The data above is used only to authenticate your access, answer your questions, keep your conversation history available to you, improve answer relevance from your feedback, and keep the service secure and operational.",
      ],
    },
    {
      title: "6. Cross-border transfers",
      paragraphs: [
        "Primary hosting is in Canada. The language-model providers and the authentication provider process data on infrastructure that may be located in the United States. Using the application entails that transfer.",
      ],
    },
    {
      title: "7. Retention",
      paragraphs: [
        "Conversations are kept until you or the operating organization delete them. Deleting a conversation in the application removes it from the database. Deleting an account removes its conversations, on request to the contact below.",
      ],
    },
    {
      title: "8. Security",
      paragraphs: [
        "All traffic to the backend uses HTTPS. Access is authorized by a short-lived authentication token held in the operating system's secure store on the device (Keychain on iOS, Keystore on Android). Two-factor authentication is enabled on the authentication service.",
      ],
    },
    {
      title: "9. Your rights",
      paragraphs: [
        `You may request access to your data, correction, deletion, or a copy of your conversations by writing to ${CONTACT_EMAIL}. We respond within a reasonable time.`,
      ],
    },
    {
      title: "10. Children",
      paragraphs: [
        "The application is a professional tool and is not directed to anyone under 16. No account is created for a minor.",
      ],
    },
    {
      title: "11. Changes",
      paragraphs: [
        "This policy may be updated. The date at the top of the page reflects the latest revision; a material change is communicated to invited users.",
      ],
    },
    {
      title: "12. Contact",
      paragraphs: [`For any question about this policy: ${CONTACT_EMAIL}.`],
    },
  ],
};

const PolicyBody: FunctionComponent<{ policy: PolicyDocument }> = ({ policy }) => (
  <section lang={policy.lang}>
    <Title level={1} className="text-noa">
      {policy.title}
    </Title>
    <Text className="text-noa-muted text-sm">{policy.lastUpdatedLabel}</Text>
    <Paragraph className="text-noa mt-4">{policy.intro}</Paragraph>

    {policy.sections.map((section) => (
      <div key={section.title} className="mt-6">
        <Title level={3} className="text-noa">
          {section.title}
        </Title>
        {section.paragraphs?.map((paragraph) => (
          <Paragraph key={paragraph} className="text-noa">
            {paragraph}
          </Paragraph>
        ))}
        {section.bullets && (
          <ul className="list-disc pl-6 text-noa">
            {section.bullets.map((bullet) => (
              <li key={bullet} className="mb-1">
                {bullet}
              </li>
            ))}
          </ul>
        )}
        {section.notes?.map((note) => (
          <Paragraph key={note} className="text-noa mt-3">
            {note}
          </Paragraph>
        ))}
      </div>
    ))}
  </section>
);

export const PrivacyPolicyScreen: FunctionComponent = () => (
  <main className="bg-noa-inset min-h-screen w-full overflow-y-auto">
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-8">
      <PolicyBody policy={FRENCH} />
      <Divider className="my-12" />
      <PolicyBody policy={ENGLISH} />
    </div>
  </main>
);
